import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import type { ListFiltersProps, SelectOption } from './types';

import { AsyncAutoComplete } from '@/components/AsyncAutoComplete';
import { DatePicker } from '@/components/DatePicker';
import { MonthPicker } from '@/components/MonthPicker';
import { MultiSelect } from '@/components/MultiSelect';
import { Select } from '@/components/Select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';

import { cn } from '@/lib/utils';

/**
 * ListFilters component
 * Renders filter fields based on configuration array
 * Each filter has its own state managed externally
 */
export const ListFilters = (props: ListFiltersProps) => {
  const {
    filters,
    defaultOpen = false,
    className,
    gridClassName,
    onApplyFilters,
    hasChanges = false,
  } = props;
  const { t } = useTranslation();

  const booleanOptions = useMemo(
    () => [
      { id: 'yes', name: t('Yes') },
      { id: 'no', name: t('No') },
    ],
    [t],
  );

  const renderFilter = (filter: (typeof filters)[0]) => {
    const {
      type,
      name,
      label,
      value,
      onChange,
      onClear,
      options,
      hideClearButton = false,
      disabled = false,
    } = filter;

    if (type === 'select') {
      const stringValue = value ? String(value) : '';

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-end gap-1"
        >
          <div className="min-w-0 flex-1">
            <Select
              value={stringValue}
              onValueChange={(newValue) => {
                onChange(newValue || null);
              }}
              options={(options || []) as SelectOption[]}
              label={label}
              disabled={disabled}
              searchable={filter.searchable}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    if (type === 'multiselect') {
      const arrayValue = (value as (string | number)[]) || [];
      const stringArrayValue = arrayValue.map((v) => String(v));

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-start gap-1"
        >
          <div className="min-w-0 flex-1">
            <MultiSelect
              label={label}
              value={stringArrayValue}
              options={(options || []).map((opt) => {
                const o = opt as SelectOption;
                return { id: o.id, name: o.name };
              })}
              onValueChange={(selectedValues) => {
                onChange(selectedValues);
              }}
              disabled={disabled}
              searchable={filter.searchable}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    if (type === 'boolean') {
      let booleanValue = '';
      if (value === true) {
        booleanValue = 'yes';
      }
      if (value === false) {
        booleanValue = 'no';
      }

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-end gap-1"
        >
          <div className="min-w-0 flex-1">
            <Select
              value={booleanValue}
              onValueChange={(newValue) => {
                if (newValue === 'yes') {
                  onChange(true);
                } else if (newValue === 'no') {
                  onChange(false);
                } else {
                  onChange(null);
                }
              }}
              options={booleanOptions}
              label={label}
              disabled={disabled}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    if (type === 'date') {
      const stringValue = value ? String(value) : '';

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-end gap-1"
        >
          <div className="min-w-0 flex-1">
            <DatePicker
              label={label}
              value={stringValue}
              onChange={(value) => {
                onChange(value || null);
              }}
              disabled={disabled}
              fromYear={new Date().getFullYear() - 10}
              toYear={new Date().getFullYear() + 10}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    if (type === 'month') {
      const stringValue = value ? String(value) : '';

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-end gap-1"
        >
          <div className="min-w-0 flex-1">
            <MonthPicker
              label={label}
              value={stringValue}
              onChange={(newValue) => {
                onChange(newValue || null);
              }}
              disabled={disabled}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    if (type === 'autocomplete') {
      const {
        fetchUrl,
        queryKey,
        transformResponse,
        getOptionLabel,
        getOptionValue,
      } = filter;

      return (
        <div
          key={name}
          className="flex w-full flex-1 grow items-end gap-1"
        >
          <div className="min-w-0 flex-1">
            <AsyncAutoComplete
              fetchUrl={fetchUrl || ''}
              queryKey={queryKey || name}
              selectedValue={value || null}
              onSelectedValueChange={(selectedOption) => {
                onChange(selectedOption);
              }}
              getOptionLabel={getOptionLabel || ((opt: unknown) => String(opt))}
              getOptionValue={getOptionValue || ((opt: unknown) => String(opt))}
              transformResponse={
                transformResponse || ((res: unknown) => res as unknown[])
              }
              label={label}
              emptyMessage={t('No results found')}
              disabled={disabled}
            />
          </div>
          {!hideClearButton && (
            <Button
              type="button"
              variant="outline"
              onClick={onClear}
              aria-label={t('Clear filter')}
              disabled={disabled}
              className="size-10"
            >
              <X className="size-4" />
            </Button>
          )}
        </div>
      );
    }

    return null;
  };

  if (filters.length === 0) {
    return null;
  }

  const filterValue = 'filter-item';

  return (
    <Accordion
      type="single"
      collapsible
      defaultValue={defaultOpen ? filterValue : undefined}
      className={cn('w-full', className)}
    >
      <AccordionItem
        value={filterValue}
        className="rounded-lg border shadow"
      >
        <AccordionTrigger className="cursor-pointer px-4 py-3 hover:no-underline">
          {t('Additional Filters')}
        </AccordionTrigger>
        <AccordionContent className="px-4 pb-4">
          <div
            className={cn(
              'grid grid-cols-1 items-start gap-4 pt-2 sm:grid-cols-2 md:grid-cols-3',
              gridClassName,
            )}
          >
            {filters.map((filter) => renderFilter(filter))}
          </div>
          {onApplyFilters && (
            <div className="mt-4 flex flex-col gap-2">
              {hasChanges && (
                <p className="text-muted-foreground text-right text-sm">
                  {t('Filters have been changed but not applied')}
                </p>
              )}
              <div className="flex justify-end">
                <Button
                  type="button"
                  onClick={onApplyFilters}
                  variant={hasChanges ? 'default' : 'outline'}
                >
                  {t('Filter')}
                </Button>
              </div>
            </div>
          )}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
